import React, { useState } from "react";
import useGetAssessmentRecommendations from "@features/assessments-feature/hooks/useGetAssessmentRecommendations";
import {
  assessmentTabViewMode,
  CACHE_KEY_ASSESSMENTS_RECOMMENDATIONS,
  sortedPriorities,
  sortedSPRv2Priorities,
} from "@features/assessments-feature/constants";
import { useAssessmentViewContext } from "@features/assessments-feature/contexts-providers/AssessmentViewContextProvider";
import ErrorIcon from "@mui/icons-material/Error";
import { Typography } from "@mui/material";
import PrioritiesRecsList from "@features/assessments-feature/components/priorities-recommendations/PrioritiesRecsList";
import PrioritiesRecsTable from "@features/assessments-feature/components/priorities-recommendations/PrioritiesRecsTable";
import DemoModeAssessmentRecs from "@features/assessments-feature/components/DemoModeAssessmentRecs";
import useAssessmentDemoData from "@features/assessments-feature/hooks/useAssessmentDemoData";
import {
  PrioritizedRecommendation,
  ResponseRecommendation,
  RoadmapItemSourceType,
} from "@lib/ShiOneClient";
import { useLocation } from "react-router-dom";
import RecommendationsDialog from "@features/assessments-feature/components/priorities-recommendations/RecommendationsDialog";
import SendToDialog from "@features/assessments-feature/components/priorities-recommendations/SendToDialog";
import { isProgramSprV2 } from "@features/assessments-feature/components/priorities-recommendations/Utils/PrioritiesRecsUtilities";

const PrioritiesRecsView = ({
  viewMode,
}: {
  viewMode: assessmentTabViewMode;
}) => {
  const { state } = useLocation();
  const {
    assessmentId,
    program,
    isOpenSendToDialog,
    assessmentViewProps,
    setIsOpenSendToDialog,
  } = useAssessmentViewContext();
  const {
    data: prioritiesData,
    isLoading,
    isError,
    isPlaceholderData,
  } = useGetAssessmentRecommendations(
    assessmentViewProps?.id,
    assessmentId ?? undefined,
  );
  const { isDemoMode } = useAssessmentDemoData();
  const isSprV2 = isProgramSprV2(program);

  const [openRecsDialog, setOpenRecsDialog] = useState(false);
  const [currentRecs, setCurrentRecs] = useState<ResponseRecommendation>();
  const handleRecsDialogOpenState = (
    recsValue: ResponseRecommendation | undefined,
    isOpen: boolean,
  ) => {
    setOpenRecsDialog(isOpen);
    setCurrentRecs(recsValue);
  };
  const loading = isLoading || isPlaceholderData;

  if (isError) {
    return (
      <>
        <ErrorIcon color={"error"} />
        <Typography component="span" color={"error"} ml={2}>
          Error loading priorities and recommendation
        </Typography>
      </>
    );
  }

  if (assessmentViewProps?.id && isDemoMode.get(assessmentViewProps?.id)) {
    return (
      <DemoModeAssessmentRecs assessmentProgram={assessmentViewProps?.id} />
    );
  }

  const sortTechnique = isSprV2 ? sortedSPRv2Priorities : sortedPriorities;

  const sortedPrioritizedRecs: PrioritizedRecommendation[] =
    prioritiesData?.prioritizedRecommendations?.sort(
      (a: PrioritizedRecommendation, b: PrioritizedRecommendation) =>
        sortTechnique[a.priority ?? "Unknown"] -
        sortTechnique[b.priority ?? "Unknown"],
    ) ?? [];

  const currentRecsFromState = sortedPrioritizedRecs
    .flatMap((capability) => capability.responseRecommendations)
    ?.find((rec) => {
      if (state?.capability && rec?.capability)
        return rec.capability === state.capability;
    });

  const isOpenRecsDialog =
    (state?.openRecsDialog || openRecsDialog) && !loading;

  return (
    <>
      {viewMode === assessmentTabViewMode.list && (
        <PrioritiesRecsList
          sortedPrioritizedRecs={sortedPrioritizedRecs}
          handleRecsDialogOpenState={handleRecsDialogOpenState}
          loading={loading}
        />
      )}

      {viewMode === assessmentTabViewMode.table && (
        <PrioritiesRecsTable
          sortedPrioritizedRecs={sortedPrioritizedRecs}
          handleRecsDialogOpenState={handleRecsDialogOpenState}
          loading={loading}
        />
      )}
      {isOpenRecsDialog && (
        <RecommendationsDialog
          openRecsDialog={isOpenRecsDialog}
          currentRecs={currentRecs || currentRecsFromState}
          handleRecsDialog={handleRecsDialogOpenState}
        />
      )}
      {isOpenSendToDialog && (
        <SendToDialog
          isOpenSendToDialog={isOpenSendToDialog}
          setIsOpenSendToDialog={setIsOpenSendToDialog}
          sourceType={RoadmapItemSourceType.AssessmentRecommendation}
          invalidateQueryKeys={[[CACHE_KEY_ASSESSMENTS_RECOMMENDATIONS]]}
        />
      )}
    </>
  );
};

export default PrioritiesRecsView;
